import React from 'react';
import { isManualProgressGoal } from '../../constants/fitnessProfileGoals';
import FitnessProfileWeightGoalPaceHint from './FitnessProfileWeightGoalPaceHint';

export default function FitnessProfileGoalProgressList({
	goals = [],
	canEdit = false,
	onUpdateProgress
}) {
	const activeGoals = goals.filter((goal) => goal.status !== 'archived');
	if (!activeGoals.length) {
		return <p className="text-sm text-gray-500 dark:text-gray-400">No goals yet. Add one to track progress.</p>;
	}
	return (
		<div className="space-y-3">
			{activeGoals.map((goal) => {
				const isMaintenance = goal.progress_kind === 'maintenance';
				const isWeightGoal =
					goal.category === 'weight_loss' || goal.category === 'weight_gain';
				const showManualProgress = isManualProgressGoal(goal);
				const barPercent = Math.max(0, Math.min(100, goal.progress ?? 0));
				const onTrack = Boolean(goal.on_track);
				const barClassName = isMaintenance
					? onTrack
						? 'bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-500'
						: 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-red-500'
					: 'bg-gradient-to-r from-green-400 via-green-500 to-green-600';

				return (
					<div key={goal.id || goal.title} className="rounded-xl border border-gray-300 dark:border-dark-border p-3">
						<div className="flex items-center justify-between gap-3">
							<div className="min-w-0">
								<div className="text-sm font-medium text-gray-900 dark:text-gray-100">{goal.title}</div>
								{goal.category_label && (
									<div className="text-xs text-gray-500 dark:text-gray-400">{goal.category_label}</div>
								)}
							</div>
							<div className="text-right shrink-0">
								{isMaintenance ? (
									<div
										className={`text-xs font-semibold ${
											onTrack
												? 'text-blue-700 dark:text-blue-300'
												: 'text-yellow-700 dark:text-yellow-300'
										}`}
									>
										{goal.progress_label || 'On track'}
									</div>
								) : (
									<div className="text-xs font-semibold text-gray-600 dark:text-gray-300">{barPercent}%</div>
								)}
							</div>
						</div>
						{goal.latest_reading && (isMaintenance || isWeightGoal) && (
							<div className="mt-1.5 text-xs text-gray-600 dark:text-gray-300">
								Latest {goal.latest_reading}
								{goal.baseline_pounds != null && isWeightGoal && (
									<span className="text-gray-400 dark:text-gray-500">
										{' '}
										· from {Math.round(goal.baseline_pounds)} lbs toward {goal.target_metric}
									</span>
								)}
								{isMaintenance && (
									<span className="text-gray-400 dark:text-gray-500"> · target {goal.target_metric}</span>
								)}
							</div>
						)}
						<div className="mt-2 h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
							<div
								className={`h-full rounded-full ${barClassName}`}
								style={{ width: `${barPercent}%` }}
							/>
						</div>
						{isWeightGoal && !isMaintenance && (
							<FitnessProfileWeightGoalPaceHint message={goal.weekly_pace_message} />
						)}
						{isWeightGoal && barPercent === 0 && goal.baseline_pounds != null && (
							<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
								{goal.latest_pounds >= goal.baseline_pounds
									? 'No loss logged yet from your starting weight.'
									: 'Keep logging weight to update progress.'}
							</p>
						)}
						{showManualProgress && barPercent === 0 && (
							<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
								Update progress when you check in on this goal.
							</p>
						)}
						{isMaintenance && goal.target_date && (
							<div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
								{barPercent}% through{' '}
								{new Date(`${goal.target_date}T12:00:00`).toLocaleDateString('en-US', {
									month: 'short',
									day: 'numeric'
								})}
							</div>
						)}
						{canEdit && showManualProgress && onUpdateProgress && (
							<button
								type="button"
								onClick={() => onUpdateProgress(goal)}
								className="mt-1.5 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
							>
								Update progress
							</button>
						)}
						{goal.note && (
							<div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{goal.note}</div>
						)}
					</div>
				);
			})}
		</div>
	);
}
